import { Dispatch, SetStateAction, useCallback, useEffect, useMemo } from 'react';
import { useApolloClient } from '@apollo/client/react';
import { useParams } from 'react-router-dom';
import { RendererContextType } from '../../../contexts/RendererContext';
import { isNotNullOrUndefined } from '../../../utils/isNotNullOrUndefined';
import { isPotreeThreeAvailable } from '../../../utils/potreeThree';
import { ProjectShellFragment, ProjectAnnotationStateQuery, ProjectAnnotationStateQueryVariables } from '../../../types/graphqlTypes';
import { CadLayer, InitialCadLayer } from '../../../contexts/CadLayersContext';
import { PROJECT_ANNOTATION_STATE } from '../../../graphql/project';
import { useAnnotationTree, useAnnotationLookup, useCalculationsByIdentifier } from '../../potree/useAnnotationContext';
import { useStableEvent } from '../../useStableEvent';
import { EventInput } from '../../../workers/OrthophotoReaderWorker';
import { EventOutput } from '../../../workers/OrthophotoReaderWorker';
import Worker from '../../../workers/OrthophotoReaderWorker?worker';
import { callWorker } from '../../../potree/utils/workers/callWorker';
import { ProjectionSystems, ProjectionSystemsByEpsgCode } from '../../../utils/constants';
import { useSurfaceProperties } from '../../../modules/Properties/integration/annotations/SurfaceProperties';
import { useWallSketchProperties } from '../../../modules/Properties/integration/annotations/WallSketchProperties';
import {
  syncFromGraphQL,
  createSyncPointAnnotationsConfig,
  createSyncDistanceAnnotationsConfig,
  createSyncAreaAnnotationsConfig,
  createSyncOpeningsConfig,
  createSyncBoxesConfig,
  createSyncPointClustersConfig,
  createSyncRoomsConfig,
  createSyncWallSketchesConfig,
  createSyncFloorMeshes3DConfig,
  createSyncOutlinesConfig,
  createSyncOpeningSketchesConfig,
  createSyncSketchObjectsConfig,
  createSyncSurfacesConfig,
  createSyncStructuresConfig,
  syncCadObjects as syncCadObjectsFn,
  createSyncClassificationsConfig,
  syncOrthophotoLayers as syncOrthophotoLayersFn,
  syncMap as syncMapFn,
  syncWmsLayers as syncWmsLayersFn,
  syncOrderedIdentifiers as syncOrderedIdentifiersFn,
} from './sync';

let epsgCache: Record<number, { proj4: string }> | null = null;
async function getEpsgIndex(): Promise<Record<number, { proj4: string }>> {
  if (!epsgCache) {
    const mod = await import('epsg-index/all.json');
    epsgCache = mod.default as unknown as Record<number, { proj4: string }>;
  }
  return epsgCache;
}

export const useSyncProjectState = ({
  project,
  cadLayers,
  setCadLayers,
  initializedPointclouds,
  wmsLayersContextValue,
  orthophotoLayersContextValue,
  viewer,
  potreeSourcesLoaded,
}: {
  project?: ProjectShellFragment;
  cadLayers: Record<string, CadLayer | InitialCadLayer | null>;
  setCadLayers: Dispatch<SetStateAction<Record<string, CadLayer | InitialCadLayer | null>>>;
  initializedPointclouds: string[];
  wmsLayersContextValue: { wmsLayers: ProjectShellFragment['state']['wmsLayers'] };
  orthophotoLayersContextValue: { orthophotoLayers: ProjectShellFragment['state']['orthophotoLayers'] };
  viewer: RendererContextType['viewer'];
  potreeSourcesLoaded?: boolean;
}) => {
  const { loading: surfacePropertiesLoading } = useSurfaceProperties(project?.id || '');
  const { loading: wallSketchPropertiesLoading } = useWallSketchProperties(project?.id || '');

  // Read annotation data from the shared context (single Apollo subscription in AnnotationContextProvider).
  // The sync callbacks read raw data from cache via refs at call time.
  const { projectId = '' } = useParams();
  const annotationTree = useAnnotationTree();
  const annotationsByIdentifierLookup = useAnnotationLookup();
  const calculationsByIdentifier = useCalculationsByIdentifier();
  // For sync functions that need raw state.annotations/groups, read from Apollo cache on demand via ref
  const apolloClient = useApolloClient();
  const readAnnotationState = useCallback(() => {
    try {
      return apolloClient.readQuery<ProjectAnnotationStateQuery, ProjectAnnotationStateQueryVariables>({
        query: PROJECT_ANNOTATION_STATE,
        variables: { projectId },
      })?.projectById;
    } catch { return undefined; }
  }, [apolloClient, projectId]);

  const getGeoTiffData = useCallback(async ({ url }: EventInput['data']) => {
    return callWorker<EventInput['data'], EventOutput>(() => new Worker(), { url });
  }, []);

  const transformGeoTiffCoordinates = useCallback(
    async (origin: number[], crs: number) => {
      const epsg = await getEpsgIndex();
      const customCrs = ProjectionSystemsByEpsgCode[crs];
      const sourceCrs = customCrs ? ProjectionSystems[customCrs as keyof typeof ProjectionSystems].parameters : epsg[crs]?.proj4;
      const targetCrs = project?.settings?.projectionSystem && ProjectionSystems[project.settings.projectionSystem as keyof typeof ProjectionSystems].parameters;
      const transformedCoordinates = sourceCrs && targetCrs ? proj4(sourceCrs, targetCrs, origin) : origin;
      return transformedCoordinates;
    },
    [project],
  );

  // --- Annotation syncs (split by sub-type) ---
  // Each sync is a `useStableEvent`: it always observes the latest render's values and never changes
  // identity, so the effects below keep re-running only when their own data slice changes. NOT React's
  // `useEffectEvent`: this hook runs inside `memo(Project_)`, where React 19.2 never refreshes effect events —
  // the syncs then stayed frozen at the first render (see useStableEvent).

  const syncPointAnnotations = useStableEvent(
    ({ viewer }: { viewer: RendererContextType['viewer'] }) => {
      const as = readAnnotationState();
      if (!as?.state.annotations || !viewer) return;
      syncFromGraphQL(
        createSyncPointAnnotationsConfig({
          viewer,
          annotations: as.state.annotations,
          groups: as.state.groups,
        }),
      );
    },
  );

  // Room folder ("tag") data for the viewport labels layer — pushed over the
  // same proven channel as the scene-object syncs (explicit viewer + state
  // read), so it can never go stale across project switches.
  const syncRoomFolderLabels = useStableEvent(
    ({ viewer }: { viewer: RendererContextType['viewer'] }) => {
      const as = readAnnotationState();
      if (!as?.state.groups || !viewer) return;
      viewer.roomLabelsLayer?.setFolders(
        as.state.groups
          .filter((g) => g.type === ('ROOM' as typeof g.type))
          .map((g) => ({ identifier: g.identifier, name: g.name, parentIdentifier: g.parentIdentifier ?? null })),
      );
    },
  );

  const syncDistanceAnnotations = useStableEvent(
    ({ viewer }: { viewer: RendererContextType['viewer'] }) => {
      const as = readAnnotationState();
      if (!as?.state.annotations || !viewer) return;
      syncFromGraphQL(
        createSyncDistanceAnnotationsConfig({
          viewer,
          annotations: as.state.annotations,
          groups: as.state.groups,
        }),
      );
    },
  );

  const syncAreaAnnotations = useStableEvent(
    ({ viewer }: { viewer: RendererContextType['viewer'] }) => {
      const as = readAnnotationState();
      if (!as?.state.annotations || !viewer) return;
      syncFromGraphQL(
        createSyncAreaAnnotationsConfig({
          viewer,
          annotations: as.state.annotations,
          groups: as.state.groups,
          calculationsByIdentifier,
        }),
      );
    },
  );

  const syncOpenings = useStableEvent(
    ({ viewer }: { viewer: RendererContextType['viewer'] }) => {
      const as = readAnnotationState();
      if (!as?.state.annotations || !viewer) return;
      syncFromGraphQL(
        createSyncOpeningsConfig({
          viewer,
          annotations: as.state.annotations,
          groups: as.state.groups,
        }),
      );
    },
  );

  // --- CAD objects ---

  const cadLayersArray = useMemo(() => Object.values(cadLayers).filter(isNotNullOrUndefined), [cadLayers]);

  const syncCadObjects = useStableEvent(
    ({ viewer }: { viewer: RendererContextType['viewer'] }) => {
      if (!cadLayers || !viewer) return;
      syncCadObjectsFn({ viewer, cadLayers, cadLayersArray, setCadLayers });
    },
  );

  // --- Boxes ---

  const syncBoxes = useStableEvent(
    ({ viewer }: { viewer: RendererContextType['viewer'] }) => {
      const as = readAnnotationState();
      if (!as?.state.annotations || !viewer) return;
      syncFromGraphQL(
        createSyncBoxesConfig({
          viewer,
          annotations: as.state.annotations,
        }),
      );
    },
  );

  // --- Point clusters ---

  const syncPointClusters = useStableEvent(
    ({ viewer }: { viewer: RendererContextType['viewer'] }) => {
      const as = readAnnotationState();
      if (!as?.state.annotations || !viewer) return;
      syncFromGraphQL(
        createSyncPointClustersConfig({
          viewer,
          annotations: as.state.annotations,
        }),
      );
    },
  );

  // --- Rooms ---

  const syncRooms = useStableEvent(
    ({ viewer }: { viewer: RendererContextType['viewer'] }) => {
      const as = readAnnotationState();
      if (!as?.state.annotations || !viewer) return;
      syncFromGraphQL(
        createSyncRoomsConfig({
          viewer,
          annotations: as.state.annotations,
          groups: as.state.groups,
        }),
      );
    },
  );

  // --- WallSketchs ---

  const syncWallSketches = useStableEvent(
    ({ viewer }: { viewer: RendererContextType['viewer'] }) => {
      const as = readAnnotationState();
      if (!as?.state.annotations || !viewer) return;
      syncFromGraphQL(
        createSyncWallSketchesConfig({
          viewer,
          annotations: as.state.annotations,
          wallSketchPropertiesLoading,
        }),
      );
    },
  );

  // --- Outlines ---

  const syncOutlines = useStableEvent(
    ({ viewer }: { viewer: RendererContextType['viewer'] }) => {
      const as = readAnnotationState();
      if (!as?.state.annotations || !viewer) return;
      syncFromGraphQL(
        createSyncOutlinesConfig({
          viewer,
          annotations: as.state.annotations,
        }),
      );
    },
  );

  const syncOpeningSketches = useStableEvent(
    ({ viewer }: { viewer: RendererContextType['viewer'] }) => {
      const as = readAnnotationState();
      if (!as?.state.annotations || !viewer) return;
      syncFromGraphQL(
        createSyncOpeningSketchesConfig({
          viewer,
          annotations: as.state.annotations,
          groups: as.state.groups,
        }),
      );
    },
  );

  const syncSketchObjects = useStableEvent(
    ({ viewer }: { viewer: RendererContextType['viewer'] }) => {
      const as = readAnnotationState();
      if (!as?.state.annotations || !viewer) return;
      syncFromGraphQL(
        createSyncSketchObjectsConfig({
          viewer,
          annotations: as.state.annotations,
          groups: as.state.groups,
        }),
      );
    },
  );

  // --- Floor 3D meshes ---
  // Driven by floor groups (not annotations) — one mesh per FLOOR group.
  // Children (outline/rooms/openings) are resolved in the sync hook so the
  // mesh class itself stays decoupled from the GraphQL shape.

  const syncFloorMeshes3D = useStableEvent(
    ({ viewer }: { viewer: RendererContextType['viewer'] }) => {
      const as = readAnnotationState();
      if (!as?.state.groups || !as?.state.annotations || !viewer) return;
      syncFromGraphQL(
        createSyncFloorMeshes3DConfig({
          viewer,
          groups: as.state.groups,
          annotations: as.state.annotations,
        }),
      );
    },
  );

  // --- Structures ---

  const syncStructures = useStableEvent(
    ({ viewer }: { viewer: RendererContextType['viewer'] }) => {
      const as = readAnnotationState();
      if (!as?.state.annotations || !viewer) return;
      syncFromGraphQL(
        createSyncStructuresConfig({
          viewer,
          annotations: as.state.annotations,
          groups: as.state.groups,
        }),
      );
    },
  );

  // --- Surfaces ---

  const syncSurfaces = useStableEvent(
    ({ viewer }: { viewer: RendererContextType['viewer'] }) => {
      const as = readAnnotationState();
      if (!as?.state.groups || !viewer) return;
      syncFromGraphQL(
        createSyncSurfacesConfig({
          viewer,
          groups: as.state.groups,
          surfacePropertiesLoading,
        }),
      );
    },
  );

  // --- Classifications ---

  const syncClassifications = useStableEvent(
    ({ viewer }: { viewer: RendererContextType['viewer'] }) => {
      if (!project?.customClasses || !viewer) return;
      syncFromGraphQL(
        createSyncClassificationsConfig({
          viewer,
          customClasses: project.customClasses,
        }),
      );
    },
  );

  // --- Map ---

  const syncMap = useStableEvent(
    ({ viewer }: { viewer: RendererContextType['viewer'] }) => {
      syncMapFn(viewer, project, initializedPointclouds);
    },
  );

  // --- WMS Layers ---

  const syncWmsLayers = useStableEvent(({ viewer }: { viewer: RendererContextType['viewer'] }) => {
    syncWmsLayersFn(viewer, project, initializedPointclouds, wmsLayersContextValue.wmsLayers);
  });

  // --- Orthophoto Layers ---

  const syncOrthophotoLayers = useStableEvent(
    ({ viewer }: { viewer: RendererContextType['viewer'] }) => {
      if (!viewer) return;
      syncOrthophotoLayersFn({
        viewer,
        project,
        initializedPointclouds,
        orthophotoLayers: orthophotoLayersContextValue.orthophotoLayers,
        getGeoTiffData,
        transformGeoTiffCoordinates,
      });
    },
  );

  // --- Ordered Identifiers ---

  const syncOrderedIdentifiers = useStableEvent(
    ({ viewer }: { viewer: RendererContextType['viewer'] }) => {
      syncOrderedIdentifiersFn(viewer, readAnnotationState()?.state.orderedIdentifiers);
    },
  );

  // Content-based keys derived from the shared annotation context (single subscription).
  // The context provides annotationsByIdentifier which has all flat annotations.
  // We derive type-specific keys from it so each sync effect only re-runs when its type changes.
  const allAnnotationIdentifiers = annotationsByIdentifierLookup;
  const allAnnotations = useMemo(() => Object.values(allAnnotationIdentifiers), [allAnnotationIdentifiers]);
  const pointAnnotationIds = useMemo(() => allAnnotations.filter((a) => a.type === 'POINT').map((a) => a.identifier).join(','), [allAnnotations]);
  const distanceAnnotationIds = useMemo(() => allAnnotations.filter((a) => a.type === 'DISTANCE').map((a) => a.identifier).join(','), [allAnnotations]);
  const areaAnnotationIds = useMemo(() => allAnnotations.filter((a) => a.type === 'AREA').map((a) => a.identifier).join(','), [allAnnotations]);
  const openingIds = useMemo(() => allAnnotations.filter((a) => a.type === 'OPENING').map((a) => a.identifier).join(','), [allAnnotations]);
  const boxAnnotationIds = useMemo(() => allAnnotations.filter((a) => a.type === 'BOX').map((a) => a.identifier).join(','), [allAnnotations]);
  const pointClusterIds = useMemo(() => allAnnotations.filter((a) => a.type === 'POINTCLUSTER').map((a) => a.identifier).join(','), [allAnnotations]);
  const roomIds = useMemo(() => allAnnotations.filter((a) => a.type === 'ROOM').map((a) => a.identifier).join(','), [allAnnotations]);
  const wallSketchIds = useMemo(() => allAnnotations.filter((a) => a.type === 'WALL_SKETCH').map((a) => a.identifier).join(','), [allAnnotations]);
  const outlineIds = useMemo(() => allAnnotations.filter((a) => a.type === 'OUTLINE').map((a) => a.identifier).join(','), [allAnnotations]);
  const openingSketchIds = useMemo(() => allAnnotations.filter((a) => a.type === 'OPENING_SKETCH').map((a) => a.identifier).join(','), [allAnnotations]);
  const sketchObjectIds = useMemo(() => allAnnotations.filter((a) => a.type === 'SKETCH_OBJECT').map((a) => a.identifier).join(','), [allAnnotations]);
  const structureIds = useMemo(() => allAnnotations.filter((a) => a.type === 'STRUCTURE').map((a) => a.identifier).join(','), [allAnnotations]);
  const groupIds = useMemo(() => annotationTree.filter((a) => 'annotations' in a).map((a) => a.identifier).join(','), [annotationTree]);
  // Room folders re-push on id/name/parent changes (renames must reach the labels).
  const roomFolderKey = useMemo(() => {
    const parts: string[] = [];
    const visit = (items: typeof annotationTree): void => {
      for (const item of items) {
        if (!('annotations' in item)) continue;
        const group = item as { identifier: string; type?: string; name?: string; parentIdentifier?: string | null; annotations: typeof annotationTree };
        if (group.type === 'ROOM') parts.push(`${group.identifier}:${group.name ?? ''}:${group.parentIdentifier ?? ''}`);
        visit(group.annotations);
      }
    };
    visit(annotationTree);
    return parts.join('|');
  }, [annotationTree]);
  const orderedIds = useMemo(() => allAnnotations.map((a) => a.identifier).join(','), [allAnnotations]);
  const customClassesKey = useMemo(
    () => (project?.customClasses ?? []).map((c) => `${c.id}:${c.name}:${c.code}:${c.color.r},${c.color.g},${c.color.b},${c.color.a}`).join('|'),
    [project?.customClasses],
  );

  // Individual sync effects — each only re-runs when its specific data slice changes.
  useEffect(() => {
    if (!viewer || !potreeSourcesLoaded || !isPotreeThreeAvailable()) return;
    syncPointAnnotations({ viewer });
  }, [viewer, potreeSourcesLoaded, pointAnnotationIds, groupIds, syncPointAnnotations]);

  useEffect(() => {
    if (!viewer || !potreeSourcesLoaded || !isPotreeThreeAvailable()) return;
    syncDistanceAnnotations({ viewer });
  }, [viewer, potreeSourcesLoaded, distanceAnnotationIds, groupIds, syncDistanceAnnotations]);

  useEffect(() => {
    if (!viewer || !potreeSourcesLoaded || !isPotreeThreeAvailable()) return;
    syncAreaAnnotations({ viewer });
  }, [viewer, potreeSourcesLoaded, areaAnnotationIds, groupIds, syncAreaAnnotations]);

  useEffect(() => {
    if (!viewer || !potreeSourcesLoaded || !isPotreeThreeAvailable()) return;
    syncOpenings({ viewer });
  }, [viewer, potreeSourcesLoaded, openingIds, groupIds, syncOpenings]);

  useEffect(() => {
    if (!viewer || !potreeSourcesLoaded || !isPotreeThreeAvailable()) return;
    syncCadObjects({ viewer });
  }, [viewer, potreeSourcesLoaded, cadLayers, cadLayersArray, syncCadObjects]);

  useEffect(() => {
    if (!viewer || !potreeSourcesLoaded || !isPotreeThreeAvailable()) return;
    syncBoxes({ viewer });
  }, [viewer, potreeSourcesLoaded, boxAnnotationIds, syncBoxes]);

  useEffect(() => {
    if (!viewer || !potreeSourcesLoaded || !isPotreeThreeAvailable()) return;
    syncPointClusters({ viewer });
  }, [viewer, potreeSourcesLoaded, pointClusterIds, syncPointClusters]);

  useEffect(() => {
    if (!viewer || !potreeSourcesLoaded || !isPotreeThreeAvailable()) return;
    syncRooms({ viewer });
  }, [viewer, potreeSourcesLoaded, roomIds, groupIds, syncRooms]);

  useEffect(() => {
    if (!viewer || !potreeSourcesLoaded || !isPotreeThreeAvailable()) return;
    syncRoomFolderLabels({ viewer });
  }, [viewer, potreeSourcesLoaded, roomFolderKey, syncRoomFolderLabels]);

  useEffect(() => {
    if (!viewer || !potreeSourcesLoaded || !isPotreeThreeAvailable() || wallSketchPropertiesLoading) return;
    syncWallSketches({ viewer });
  }, [viewer, potreeSourcesLoaded, wallSketchIds, wallSketchPropertiesLoading, syncWallSketches]);

  useEffect(() => {
    if (!viewer || !potreeSourcesLoaded || !isPotreeThreeAvailable()) return;
    syncOutlines({ viewer });
  }, [viewer, potreeSourcesLoaded, outlineIds, syncOutlines]);

  useEffect(() => {
    if (!viewer || !potreeSourcesLoaded || !isPotreeThreeAvailable()) return;
    syncOpeningSketches({ viewer });
  }, [viewer, potreeSourcesLoaded, openingSketchIds, wallSketchIds, syncOpeningSketches]);

  useEffect(() => {
    if (!viewer || !potreeSourcesLoaded || !isPotreeThreeAvailable()) return;
    syncSketchObjects({ viewer });
  }, [viewer, potreeSourcesLoaded, sketchObjectIds, wallSketchIds, syncSketchObjects]);

  useEffect(() => {
    if (!viewer || !potreeSourcesLoaded || !isPotreeThreeAvailable() || surfacePropertiesLoading) return;
    syncSurfaces({ viewer });
  }, [viewer, potreeSourcesLoaded, groupIds, surfacePropertiesLoading, syncSurfaces]);

  useEffect(() => {
    if (!viewer || !potreeSourcesLoaded || !isPotreeThreeAvailable()) return;
    syncStructures({ viewer });
  }, [viewer, potreeSourcesLoaded, structureIds, groupIds, syncStructures]);

  useEffect(() => {
    if (!viewer || !potreeSourcesLoaded || !isPotreeThreeAvailable()) return;
    syncFloorMeshes3D({ viewer });
  }, [viewer, potreeSourcesLoaded, groupIds, outlineIds, roomIds, openingIds, syncFloorMeshes3D]);

  useEffect(() => {
    if (!viewer || !potreeSourcesLoaded || !isPotreeThreeAvailable()) return;
    syncClassifications({ viewer });
  }, [viewer, potreeSourcesLoaded, customClassesKey, syncClassifications]);

  useEffect(() => {
    if (!viewer || !potreeSourcesLoaded || !isPotreeThreeAvailable()) return;
    syncMap({ viewer });
  }, [viewer, potreeSourcesLoaded, initializedPointclouds, project, syncMap]);

  useEffect(() => {
    if (!viewer || !potreeSourcesLoaded || !isPotreeThreeAvailable()) return;
    syncWmsLayers({ viewer });
  }, [viewer, potreeSourcesLoaded, initializedPointclouds, wmsLayersContextValue.wmsLayers, syncWmsLayers]);

  useEffect(() => {
    if (!viewer || !potreeSourcesLoaded || !isPotreeThreeAvailable()) return;
    syncOrthophotoLayers({ viewer });
  }, [viewer, potreeSourcesLoaded, initializedPointclouds, orthophotoLayersContextValue.orthophotoLayers, syncOrthophotoLayers]);

  useEffect(() => {
    if (!viewer || !potreeSourcesLoaded || !isPotreeThreeAvailable()) return;
    syncOrderedIdentifiers({ viewer });
  }, [viewer, potreeSourcesLoaded, orderedIds, syncOrderedIdentifiers]);
};
